import { LEVEL_UP_SEEN_HOOK, SWORD_SWISH_HOOK } from "./channels.ts";
import { thingHook } from "./things.ts";

/**
 * Shaping at build (ADR-085 decision 5): a hook may name one shaping the build applies to its masters before it
 * measures their loudness, so the folder's target is met on the shaped sound. The master on disk is never altered.
 * The table is the build's, beside `LOUDNESS_TARGETS`; the mixer reads none of it.
 */

/**
 * The shapes the build knows, by id. A low cut is a high-pass: it removes what lies under its frequency. A trim keeps
 * one stretch of the master and fades its end out.
 */
export const ShapingKind = { LowCut: "low-cut", Trim: "trim" } as const;
export type ShapingKind = (typeof ShapingKind)[keyof typeof ShapingKind];

/**
 * A low cut: the frequency in Hz under which the sound is removed.
 */
export interface LowCutShape {
  readonly kind: typeof ShapingKind.LowCut;
  readonly frequency: number;
}

/**
 * A trim, in whole milliseconds: the built sound is the master from `startMs` for `lengthMs`, its last `fadeMs`
 * fading to silence. A master shorter than the stretch ends where it ends, with as much of the fade as it reaches.
 * It is a cut in time and changes no level: the level a hook plays at is `HOOK_TRIMS` in `channels.ts`, which shares
 * only the word. The build refuses a trim whose numbers are not whole, a start under 0, a length of 0 or less, or a
 * fade under 0 or longer than the length.
 */
export interface TrimShape {
  readonly kind: typeof ShapingKind.Trim;
  readonly startMs: number;
  readonly lengthMs: number;
  readonly fadeMs: number;
}

/**
 * One shape.
 */
export type Shape = LowCutShape | TrimShape;

/**
 * One entry: the hook named `folder`, or every hook in the folder of that name, is built with `shape`.
 */
export interface Shaping {
  readonly folder: string;
  readonly shape: Shape;
}

/**
 * Where the sword's swish is cut, in Hz: lower keeps more of its bass, higher leaves it thinner.
 */
export const SWORD_SWISH_LOW_CUT_HZ = 220;

/**
 * Where the chime another player's level-up plays is cut, in Hz: it repeats whenever a player nearby levels, so it keeps
 * no bass (the author's rule for repeated effects).
 */
export const LEVEL_UP_SEEN_LOW_CUT_HZ = 400;

/**
 * Where the spider's hurt is cut, in Hz (the author, 2026-10-10: "we need to cut the bass out of spider_hit_by_sword"):
 * lower keeps more of its bass, higher leaves it thinner.
 */
export const SPIDER_HURT_LOW_CUT_HZ = 250;

/**
 * How long a well's bucket takes to come up or go down, in milliseconds: the pack's water strip, 29 frames of 100 ms
 * (ADR-042's amendment of 2026-10-03). The well's sounds fade out from here, so neither runs on long after the bucket
 * has stopped and nothing is cut while it moves on the water strip. The empty bucket's strip is one frame longer, so
 * its last 100 ms falls in the fade.
 */
export const WELL_BUCKET_STRIP_MS = 2900;

/**
 * The well's raise: the master from 500 ms in, so the water and the full bucket are heard as the bucket starts up,
 * fading out over 600 ms from the strip's end.
 */
const WELL_RAISE_TRIM: TrimShape = Object.freeze({ kind: ShapingKind.Trim, startMs: 500, lengthMs: WELL_BUCKET_STRIP_MS + 600, fadeMs: 600 });

/**
 * The well's lower: the master from 500 ms in, past its quiet lead-in, so the rope sounds as the bucket starts down
 * and the bucket meets the water as the strip ends, fading out over the 400 ms after.
 */
const WELL_LOWER_TRIM: TrimShape = Object.freeze({ kind: ShapingKind.Trim, startMs: 500, lengthMs: WELL_BUCKET_STRIP_MS + 400, fadeMs: 400 });

/**
 * The shapings: the sword's swish loses what lies under `SWORD_SWISH_LOW_CUT_HZ`, the spider's hurt what lies under
 * `SPIDER_HURT_LOW_CUT_HZ` and the level-up chime what lies under `LEVEL_UP_SEEN_LOW_CUT_HZ`, and the well's raise and lower are
 * trimmed to the bucket's strip. A trim's times are those of the recording it was measured on, so a new variant of a
 * trimmed hook is recorded to start at once and to last about as long as the strip.
 */
export const SHAPINGS: readonly Shaping[] = Object.freeze([
  { folder: SWORD_SWISH_HOOK, shape: Object.freeze({ kind: ShapingKind.LowCut, frequency: SWORD_SWISH_LOW_CUT_HZ }) },
  { folder: "sfx/creatures/spider/hurt", shape: Object.freeze({ kind: ShapingKind.LowCut, frequency: SPIDER_HURT_LOW_CUT_HZ }) },
  { folder: LEVEL_UP_SEEN_HOOK, shape: Object.freeze({ kind: ShapingKind.LowCut, frequency: LEVEL_UP_SEEN_LOW_CUT_HZ }) },
  { folder: thingHook("well", "raise"), shape: WELL_RAISE_TRIM },
  { folder: thingHook("well", "lower"), shape: WELL_LOWER_TRIM },
]);

/**
 * The shape a hook's masters are built with.
 *
 * @param hook The hook, or a music track's folder and slug.
 * @param shapings The table; `SHAPINGS` by default.
 * @returns The shape of the first entry that names the hook or a folder the hook lies in, or null when none does.
 */
export function shapeOf(hook: string, shapings: readonly Shaping[] = SHAPINGS): Shape | null {
  for (const entry of shapings) {
    if (hook === entry.folder || hook.startsWith(`${entry.folder}/`)) {
      return entry.shape;
    }
  }
  return null;
}

/**
 * A shape as text for a built file's hash: its kind and its numbers, a low cut's frequency or a trim's start, length
 * and fade, so a change to any names a new file.
 *
 * @param shape The shape.
 * @returns The key.
 */
export function shapeKey(shape: Shape): string {
  return shape.kind === ShapingKind.Trim ? `${shape.kind} ${shape.startMs} ${shape.lengthMs} ${shape.fadeMs}` : `${shape.kind} ${shape.frequency}`;
}
